import { useCallback, useMemo, useState } from "react"
import { enqueueSnackbar } from "notistack"
import { Grid } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { useNavigate } from "react-router-dom"
import { faCopy, faFileExport } from "@fortawesome/pro-light-svg-icons"
import { useQueryClient } from "@tanstack/react-query"
import { renderRowActionMenuItems } from "../components/tableElements/renderRowActionMenuItems"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import {
    MerchantProductsQuery,
    MoveOrCopyToPriceListBody,
    fetchProducts,
    moveOrCopyToPriceList,
    updateStock,
} from "../productsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { useMerchant } from "../../../../merchants/merchantHooks"
import {
    DefaultPreviewProductImageStateType,
    PreviewProductImage,
    defaultPreviewProductImageState,
} from "../components/tableElements/PreviewProductImage"
import {
    DefaultConfirmModalStateType,
    DeleteProductDialog,
    defaultConfirmModalState,
} from "../components/tableElements/DeleteProductDialog"
import { transformProductsDataWithSortOrder } from "../helpers"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { MRT_ColumnDef, MRT_RowSelectionState, type MRT_SortingState } from "material-react-table"
import { ProductItem, ProductStockInfo, ProductTableRow, UpdateStockPayload } from "../productsTypes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { ProductGroupItemBox, ProductName } from "../ProductTableColumns"
import { StockPopover } from "../components/stockManagement/StockPopover"
import { useExportProducts } from "../hooks/useExportProducts"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { ActionBarItem, FloatingActionBar } from "../../../../../shared/components/table/FloatingActionBar"
import { MoveOrCopyToPriceListDialog } from "../components/MoveOrCopyToPriceListDialog"
import { useMatchEnvironment } from "../../../../environment/envHooks"
import { EnvironmentType } from "../../../../environment/envTypes"

const useStyles = makeStyles()((theme) => ({
    hideRowActions: {
        "& .MuiIconButton-root": {
            display: "none",
        },
    },
    deleteItem: {
        color: theme.palette.tailwind.red[600],
    },
    actionBarItem: {
        width: "fit-content",
    },
}))

type StockPopoverState = Readonly<{
    anchorEl: HTMLElement | null
    productId: number | null
    stock: ProductStockInfo | null
}>

const defaultStockPopoverState: StockPopoverState = {
    anchorEl: null,
    productId: null,
    stock: null,
}

type MoveOrCopyDialogState = Readonly<{ open: false } | { open: true; type: "move" | "copy" }>

type AllProductsTabProps = Readonly<{
    priceListId: number | "NONE"
}>

export function AllProductsTab({ priceListId }: AllProductsTabProps) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const navigate = useNavigate()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const queryClient = useQueryClient()

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const [previewImage, setPreviewImage] = useState<DefaultPreviewProductImageStateType>(
        defaultPreviewProductImageState,
    )

    const [stockPopoverState, setStockPopoverState] = useState<StockPopoverState>(defaultStockPopoverState)

    const [sorting, setSorting] = useState<MRT_SortingState>([])
    const [globalFilter, setGlobalFilter] = useState<string>("")
    const [showGlobalFilter, setShowGlobalFilter] = useState(false)
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [moveOrCopyDialog, setMoveOrCopyDialog] = useState<MoveOrCopyDialogState>({ open: false })
    const [isActionLoading, setIsActionLoading] = useState(false)

    const allowActionBar = !useMatchEnvironment([EnvironmentType.Prod])

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            priceListId,
            deliveryDate: dateTimeFactory.toDate(dateTimeFactory.now()),
        },
        select: transformProductsDataWithSortOrder,
    })

    const { mutateAsync: callUpdateStock } = useApiMutation(updateStock, {
        async onSuccess(_params, _data, context) {
            enqueueSnackbar({
                variant: "success",
                message: l10n.getString("product-stock-update-success"),
            })
            // to prevent popover shifting on table refresh
            setTimeout(() => {
                context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            }, 300)
        },
    })

    const { mutateAsync: callMoveOrCopyToPriceList } = useApiMutation(moveOrCopyToPriceList, {})

    const handleStockChipClick = useCallback(
        (event: React.MouseEvent<HTMLElement>, productId: number) => {
            const productRow = products.data?.find((row) => row.type === "product" && row.id === productId)
            if (productRow?.type === "product" && productRow.stock) {
                setStockPopoverState({
                    anchorEl: event.currentTarget,
                    productId,
                    stock: productRow.stock,
                })
            }
        },
        [products.data],
    )

    const handleStockUpdate = useCallback((payload: UpdateStockPayload) => callUpdateStock(payload), [callUpdateStock])

    const exportProducts = useExportProducts({ data: products.data })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const selectedProducts = useMemo<ProductItem[]>(() => {
        const selectedIds = new Set(
            Object.entries(rowSelection)
                .filter(([, selected]) => selected)
                .map(([id]) => Number(id)),
        )
        return (products.data ?? []).filter(
            (row): row is ProductItem => row.type === "product" && selectedIds.has(row.id),
        )
    }, [products.data, rowSelection])

    const getGroupSelectionState = useCallback(
        (groupId: number): "none" | "some" | "all" => {
            const groupProducts = (products.data ?? []).filter(
                (row): row is ProductItem => row.type === "product" && row.groupId === groupId,
            )
            if (groupProducts.length === 0) return "none"
            const selectedCount = groupProducts.filter((p) => rowSelection[String(p.id)]).length
            if (selectedCount === 0) return "none"
            if (selectedCount === groupProducts.length) return "all"
            return "some"
        },
        [products.data, rowSelection],
    )

    const handleGroupSelectionToggle = useCallback(
        (groupId: number) => {
            const groupProducts = (products.data ?? []).filter(
                (row): row is ProductItem => row.type === "product" && row.groupId === groupId,
            )
            const anySelected = groupProducts.some((p) => rowSelection[String(p.id)])
            setRowSelection((prev) => {
                const next = { ...prev }
                for (const p of groupProducts) {
                    if (anySelected) {
                        delete next[String(p.id)]
                    } else {
                        next[String(p.id)] = true
                    }
                }
                return next
            })
        },
        [products.data, rowSelection],
    )

    const handleMoveOrCopyConfirm = useCallback(
        async (targetPriceListId: number) => {
            const isCopy = moveOrCopyDialog.open && moveOrCopyDialog.type === "copy"
            setIsActionLoading(true)
            try {
                const body: MoveOrCopyToPriceListBody = {
                    productOperation: {
                        type: isCopy ? "COPY" : "MOVE",
                        destination: { priceList: { id: targetPriceListId } },
                        productIds: selectedProducts.map((p) => p.id),
                    },
                }
                const result = await callMoveOrCopyToPriceList(body)
                enqueueSnackbar({
                    variant: "success",
                    message: result.serviceResponse.displayMessage,
                })
                setRowSelection({})
                setMoveOrCopyDialog({ open: false })
                queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            } finally {
                setIsActionLoading(false)
            }
        },
        [selectedProducts, moveOrCopyDialog, callMoveOrCopyToPriceList, queryClient],
    )

    const columns = useMemo<MRT_ColumnDef<ProductTableRow>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                header: l10n.getString("products-name-column"),
                size: COLUMN_SIZE.L,
                muiTableBodyCellProps: ({ row }) => ({
                    sx: row.original.type === "product" ? { paddingLeft: "2.5rem" } : {},
                }),
                Cell: ({ row }) => {
                    if (row.original.type === "group") {
                        return <ProductGroupItemBox productGroup={row.original} />
                    }

                    if (row.original.type === "product") {
                        return (
                            <ProductName
                                product={row.original}
                                onProductPhotoClick={(imageUrl) => {
                                    setPreviewImage({ show: true, image: imageUrl })
                                }}
                                onStockChipClick={handleStockChipClick}
                                activeStockProductId={stockPopoverState.productId}
                            />
                        )
                    }

                    return ""
                },
            }),
            columnFactory.moneyColumn(
                {
                    id: "price",
                    header: l10n.getString("products-price-column"),
                    size: COLUMN_SIZE.SS,
                    maxSize: COLUMN_SIZE.SS,
                },
                (row) => (row.type === "group" ? undefined : row.price),
                true,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("products-status-column"),
                    size: COLUMN_SIZE.XXS,
                    maxSize: COLUMN_SIZE.XXS,
                },
                (row) => row.isActive ?? false,
                {
                    cellOverride: (row) => (row.type === "group" ? "" : null),
                },
            ),
        ],
        [l10n, columnFactory, setPreviewImage, handleStockChipClick, stockPopoverState.productId],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                state={{ sorting, globalFilter, showGlobalFilter, rowSelection }}
                onSortingChange={setSorting}
                onGlobalFilterChange={setGlobalFilter}
                onShowGlobalFilterChange={setShowGlobalFilter}
                onRowSelectionChange={setRowSelection}
                getRowId={(row) => String(row.id)}
                enableRowSelection={(row) => row.original.type === "product"}
                muiSelectAllCheckboxProps={({ table }) => ({
                    onChange: (e) => {
                        if (table.getIsSomeRowsSelected()) {
                            setRowSelection({})
                        } else {
                            table.getToggleAllRowsSelectedHandler()(e)
                        }
                    },
                })}
                muiSelectCheckboxProps={({ row }) => {
                    if (row.original.type !== "group") return {}
                    const state = getGroupSelectionState(row.original.id)
                    return {
                        disabled: false,
                        checked: state === "all",
                        indeterminate: state === "some",
                        onChange: () => handleGroupSelectionToggle(row.original.id),
                    }
                }}
                displayColumnDefOptions={{
                    "mrt-row-select": {
                        size: 45,
                        maxSize: 45,
                    },
                }}
                muiTopToolbarProps={{ sx: { minHeight: "4rem" } }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton onClick={() => exportProducts()} />
                    </StandardTableFilter>
                )}
                renderRowActionMenuItems={({ row, closeMenu }) =>
                    renderRowActionMenuItems({
                        row,
                        closeMenu,
                        setConfirmModalState,
                        navigate,
                        errorClasses: classes.deleteItem,
                    })
                }
                muiTableBodyRowProps={({ row }) => ({
                    className: row.original.type === "group" ? classes.hideRowActions : "",
                })}
            />

            {/* Floating action bar for selected products */}
            {allowActionBar && selectedProducts.length > 0 && (
                <FloatingActionBar
                    selectedItemsLength={selectedProducts.length}
                    handleCloseActionBar={() => setRowSelection({})}
                >
                    <ActionBarItem
                        containerStyle={classes.actionBarItem}
                        icon={faFileExport}
                        title={l10n.getString("products-action-bar-move-to-price-list")}
                        onClick={() => setMoveOrCopyDialog({ open: true, type: "move" })}
                    />
                    <ActionBarItem
                        containerStyle={classes.actionBarItem}
                        icon={faCopy}
                        title={l10n.getString("products-action-bar-copy-to-price-list")}
                        onClick={() => setMoveOrCopyDialog({ open: true, type: "copy" })}
                    />
                </FloatingActionBar>
            )}

            {/* Move / Copy dialog */}
            {moveOrCopyDialog.open && (
                <MoveOrCopyToPriceListDialog
                    open={moveOrCopyDialog.open}
                    onClose={() => setMoveOrCopyDialog({ open: false })}
                    type={moveOrCopyDialog.type}
                    selectedProducts={selectedProducts}
                    onConfirm={handleMoveOrCopyConfirm}
                    isLoading={isActionLoading}
                />
            )}

            {/* Confirm delete */}
            {confirmModalState.showConfirmModal && (
                <DeleteProductDialog
                    confirmModalState={confirmModalState}
                    setConfirmModalState={setConfirmModalState}
                />
            )}

            {/* Product preview */}
            {previewImage.show && <PreviewProductImage previewImage={previewImage} setPreviewImage={setPreviewImage} />}

            {/* Stock popover */}
            {stockPopoverState.anchorEl && stockPopoverState.productId && stockPopoverState.stock && (
                <StockPopover
                    anchorEl={stockPopoverState.anchorEl}
                    onClose={() => setStockPopoverState(defaultStockPopoverState)}
                    productId={stockPopoverState.productId}
                    currentStock={stockPopoverState.stock}
                    onStockUpdate={handleStockUpdate}
                />
            )}
        </Grid>
    )
}
